import React from 'react';
import type { SvgIconProps } from '@mui/material';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { COLUMNS } from './ResultsTable';

export interface WaitBox {
  label: string;
  glyph: React.ComponentType<SvgIconProps>;
  tail: string;
  eval: boolean;
}

/** The results toolbar as it will appear, inert until the answer lands. */
export interface WaitToolbar {
  placeholder: string;
  /** result-set tabs; the first reads as selected */
  tabs?: string[];
  actions?: { label: string; icon: React.ReactNode }[];
}

export interface WaitColumn {
  label: string;
  width?: 0 | 1 | 2;
  className?: string;
}

const productBoxes = (riskLabel: string): WaitBox[] => [
  { label: riskLabel, glyph: ShieldOutlinedIcon, tail: '60%', eval: true },
  {
    label: 'Trade burden',
    glyph: BalanceOutlinedIcon,
    tail: '44%',
    eval: true,
  },
  {
    label: 'Lists matched',
    glyph: FormatListBulletedIcon,
    tail: '70%',
    eval: false,
  },
];

const PRODUCT_CELLS: Record<string, Omit<WaitColumn, 'label'>> = {
  name: { width: 0 },
  hs: { className: 'psc-skel--code' },
  list: { width: 1 },
  agency: { width: 2 },
  country: { className: 'psc-skel--country' },
  date: { className: 'psc-skel--date' },
};

const productColumns: WaitColumn[] = COLUMNS.map((c) => ({
  label: c.label,
  ...PRODUCT_CELLS[c.key],
}));

const ROWS = [
  ['62%', '70%', '52%'],
  ['48%', '58%', '38%'],
  ['70%', '64%', '46%'],
  ['54%', '76%', '40%'],
  ['66%', '52%', '50%'],
  ['44%', '68%', '36%'],
  ['58%', '60%', '44%'],
  ['50%', '72%', '48%'],
];

interface Props {
  runLine: string;
  secs: number;
  showEval: boolean;
  riskLabel: string;
  boxes?: WaitBox[];
  columns?: WaitColumn[];
  toolbar?: WaitToolbar;
}

const ScreeningWait: React.FC<Props> = ({
  runLine,
  secs,
  showEval,
  riskLabel,
  boxes,
  columns = productColumns,
  toolbar,
}) => (
  <div className='psc-wait' aria-busy='true'>
    <div className='psc-waitline'>
      <span role='status'>{runLine}</span>
      <span className='psc-spacer' />
      <span className='psc-elapsed tnum'>{secs}s elapsed</span>
    </div>
    <div className='psc-track'>
      <span />
    </div>
    <div className={`psc-boxes${showEval ? '' : ' psc-boxes--lists-only'}`}>
      {(boxes || productBoxes(riskLabel))
        .filter((b) => showEval || !b.eval)
        .map((b) => (
          <div key={b.label} className='psc-box psc-box--wait'>
            <div className='psc-bh'>
              <span className='glyph'>
                <b.glyph sx={{ fontSize: 15 }} />
              </span>
              <div className='label'>{b.label}</div>
              <div className='verdict-row'>
                <div className='psc-skel psc-skel--verdict' />
                <div className='psc-skel psc-skel--sub' />
              </div>
            </div>
            <div className='psc-skel-lines'>
              <div className='psc-skel psc-skel--line' />
              <div className='psc-skel psc-skel--line' />
              <div className='psc-skel psc-skel--line' />
              <div
                className='psc-skel psc-skel--line'
                style={{ width: b.tail }}
              />
            </div>
            <div className='psc-box-foot'>
              <div className='psc-skel psc-skel--foot' />
            </div>
          </div>
        ))}
    </div>
    {toolbar && (
      <div className='psc-toolbar psc-toolbar--wait' aria-hidden='true'>
        <div className='psc-search'>
          <span className='glyph'>
            <SearchIcon sx={{ fontSize: 18 }} />
          </span>
          <input
            type='search'
            placeholder={toolbar.placeholder}
            tabIndex={-1}
            disabled
          />
        </div>
        {toolbar.tabs && (
          <div className='psc-seg psc-seg--tabs'>
            {toolbar.tabs.map((tab, i) => (
              <button
                type='button'
                key={tab}
                aria-pressed={i === 0}
                tabIndex={-1}
                disabled
              >
                {tab}
                <span className='psc-skel psc-skel--count' />
              </button>
            ))}
          </div>
        )}
        <span className='psc-spacer' />
        <button type='button' className='psc-btn' tabIndex={-1} disabled>
          <FilterListIcon sx={{ fontSize: 18 }} />
          Filters
        </button>
        {toolbar.actions?.map((a) => (
          <button
            type='button'
            key={a.label}
            className='psc-btn'
            tabIndex={-1}
            disabled
          >
            {a.icon}
            {a.label}
          </button>
        ))}
        <button
          type='button'
          className='psc-btn psc-btn--icon'
          tabIndex={-1}
          disabled
        >
          <RefreshIcon sx={{ fontSize: 18 }} />
        </button>
      </div>
    )}
    <div className='entity-results-table-card psc-table-card psc-table-card--wait'>
      <div className='entity-results-table-scroll'>
        <table
          className={`entity-results-table${
            columns === productColumns ? ' psc-table' : ''
          }`}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.label}>
                  <span className='entity-results-sort-header'>{c.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.join('-')}>
                {columns.map((c) => (
                  <td key={c.label}>
                    <div
                      className={`psc-skel psc-skel--line${
                        c.className ? ` ${c.className}` : ''
                      }`}
                      style={
                        c.width === undefined
                          ? undefined
                          : { width: r[c.width] }
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export default ScreeningWait;
